import { Link } from "react-router-dom";
import { useColumnists } from "@/hooks/usePublicArticles";
import { useArticlesByAuthor } from "@/hooks/usePublicArticles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";
import type { Article } from "@/mocks/articles";

const socialIcons: Record<string, string> = {
  twitter: "ri-twitter-x-line",
  linkedin: "ri-linkedin-line",
  instagram: "ri-instagram-line",
  github: "ri-github-line",
  facebook: "ri-facebook-line",
  youtube: "ri-youtube-line",
};

function ColumnistLatestArticles({ slug }: { slug: string }) {
  const { articles, loading } = useArticlesByAuthor(slug, 2);

  if (loading) {
    return (
      <div className="mt-5 pt-5 border-t border-hj-gray-border space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg bg-hj-gray-light animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (articles.length === 0) return null;

  return (
    <div className="mt-5 pt-5 border-t border-hj-gray-border">
      <p className="text-xs font-bold uppercase tracking-wider text-hj-gray mb-3 font-body">
        Neueste Kolumnen
      </p>
      <div className="space-y-3">
        {articles.map((col: Article) => (
          <Link
            key={col.id}
            to={`/artikel/${col.slug}`}
            className="flex items-start gap-3 group/col"
          >
            <div className="w-16 h-12 rounded overflow-hidden shrink-0">
              <img
                src={col.imageUrl}
                alt={col.imageAlt || col.title}
                loading="lazy"
                width={64}
                height={48}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-hj-dark group-hover/col:text-hj-red transition-colors line-clamp-2 font-heading">
                {col.title}
              </h4>
              <span className="text-xs text-hj-muted font-body">
                {new Date(col.publishedAt).toLocaleDateString("de-CH", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ColumnistsPage() {
  const { columnists, loading, error } = useColumnists();

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
        <div className="bg-hj-navy text-white py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="h-10 w-64 bg-white/10 rounded animate-pulse mb-3"></div>
            <div className="h-6 w-full max-w-xl bg-white/10 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-64 rounded-xl bg-hj-gray-light animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white pt-[100px] md:pt-[110px] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Fehler beim Laden</h1>
          <p className="text-hj-gray mb-4 font-body">{error}</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-[110px] md:pt-[130px]">
      {/* Header */}
      <div className="bg-hj-navy text-white py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-4 font-body">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white">Kolumnisten</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold font-heading">Unsere Kolumnisten</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base md:text-lg font-body leading-relaxed">
            Erfahrene Stimmen mit klaren Positionen. Unsere Kolumnisten analysieren, kommentieren und provozieren – jede Woche neu.
            Tiefgründige Perspektiven auf Politik, Wirtschaft und Gesellschaft.
          </p>
        </div>
      </div>

      {/* Mobile ad above columnists grid */}
      <div className="md:hidden max-w-7xl mx-auto px-4 pt-4">
        <AdSlotDisplay position="mobile_above_authors" />
      </div>

      {/* Columnists Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16">
        {columnists.length === 0 ? (
          <div className="text-center py-16 bg-hj-gray-light rounded-xl">
            <i className="ri-user-star-line text-4xl text-hj-gray mb-3"></i>
            <p className="text-hj-gray font-body">Noch keine Kolumnisten vorhanden.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {columnists.map((columnist) => (
              <div
                key={columnist.slug}
                className="group bg-white rounded-xl border border-hj-gray-border overflow-hidden hover:border-hj-red/30 hover:shadow-lg transition-all"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shrink-0 border-3 border-hj-gray-border group-hover:border-hj-red/30 transition-colors bg-hj-gray-light">
                      {columnist.image ? (
                        <img
                          src={columnist.image}
                          alt={columnist.name}
                          loading="lazy"
                          width={96}
                          height={96}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-hj-gray">
                          <i className="ri-user-line text-3xl"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg md:text-xl font-bold text-hj-dark group-hover:text-hj-red transition-colors font-heading">
                          {columnist.name}
                        </h3>
                        <span className="px-2 py-0.5 bg-hj-red/10 rounded text-hj-red text-xs font-semibold">
                          Kolumnist
                        </span>
                      </div>
                      <p className="text-sm text-hj-red font-semibold font-body">{columnist.role}</p>
                      <p className="text-sm text-hj-gray mt-2 leading-relaxed line-clamp-3 font-body">
                        {columnist.shortBio}
                      </p>

                      {columnist.socialLinks.length > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                          {columnist.socialLinks.map((link, i) => (
                            <a
                              key={i}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors text-hj-gray"
                              title={link.platform}
                            >
                              <i className={`${socialIcons[link.platform] || "ri-link"} text-sm`}></i>
                            </a>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-3 text-xs text-hj-muted font-body">
                        <span className="flex items-center gap-1">
                          <i className="ri-article-line"></i>
                          {columnist.articleCount || 0} Kolumnen
                        </span>
                      </div>
                    </div>
                  </div>

                  <ColumnistLatestArticles slug={columnist.slug} />
                </div>

                <div className="px-6 md:px-8 py-4 border-t border-hj-gray-border bg-hj-gray-light/50">
                  <Link
                    to={`/autor/${columnist.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-hj-red hover:underline font-body"
                  >
                    Alle Kolumnen von {columnist.name.split(" ")[0]} ansehen
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-hj-gray-light py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-hj-dark mb-3 font-heading">
            Möchten Sie Kolumnist werden?
          </h2>
          <p className="text-hj-gray mb-6 font-body">
            Wir suchen immer nach neuen Stimmen mit klaren Positionen. Wenn Sie Erfahrung im Journalismus haben und die Schweiz aus einer einzigartigen Perspektive betrachten, freuen wir uns auf Ihre Bewerbung.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-6 py-3 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors"
          >
            <i className="ri-mail-line"></i>
            Bewerbung senden
          </Link>
        </div>
      </div>
    </div>
  );
}